/**
 * The Mac path for local speech: record 16 kHz mono PCM in the browser, send it sealed to your
 * own Mac (`call.audio`, device-signed and end-to-end encrypted like every command), and get the
 * whisper transcript back in the command result. Nothing is sent while you aren't speaking;
 * one utterance is at most 9 frames of ≤3.5 s (30 s).
 */
import {
  CALL_AUDIO_FRAME_MAX_BYTES,
  CALL_AUDIO_FRAMES_MAX,
  CALL_AUDIO_SAMPLE_RATE,
  type CommandPayload,
} from '@viko/protocol'

/** Linear-interpolated resample of mono Float32 [-1, 1] to 16 kHz Int16. */
export function downsampleTo16k(input: Float32Array, rate: number): Int16Array {
  const ratio = rate / CALL_AUDIO_SAMPLE_RATE
  const n = Math.floor(input.length / ratio)
  const out = new Int16Array(n)
  for (let i = 0; i < n; i++) {
    const x = i * ratio
    const i0 = Math.floor(x)
    const i1 = Math.min(i0 + 1, input.length - 1)
    const s = (input[i0] ?? 0) + ((input[i1] ?? 0) - (input[i0] ?? 0)) * (x - i0)
    const c = Math.max(-1, Math.min(1, s))
    out[i] = c < 0 ? c * 0x8000 : c * 0x7fff
  }
  return out
}

/** Little-endian bytes of Int16 PCM, split into frames of at most `maxBytes`. */
export function pcmFrames(pcm: Int16Array, maxBytes = CALL_AUDIO_FRAME_MAX_BYTES): Uint8Array[] {
  const bytes = new Uint8Array(pcm.length * 2)
  const view = new DataView(bytes.buffer)
  for (let i = 0; i < pcm.length; i++) view.setInt16(i * 2, pcm[i] ?? 0, true)
  const frames: Uint8Array[] = []
  const step = maxBytes - (maxBytes % 2)
  for (let at = 0; at < bytes.length; at += step) frames.push(bytes.subarray(at, at + step))
  return frames.length ? frames.slice(0, CALL_AUDIO_FRAMES_MAX) : [new Uint8Array(0)]
}

export function base64(bytes: Uint8Array): string {
  let s = ''
  const CHUNK = 0x8000
  for (let i = 0; i < bytes.length; i += CHUNK)
    s += String.fromCharCode(...bytes.subarray(i, i + CHUNK))
  return btoa(s)
}

export type AudioSend = (
  payload: CommandPayload<'call.audio'>,
) => Promise<{ text?: string; pending?: boolean } | undefined>

/** Send one utterance; resolves to the Mac's transcript ('' when nothing was heard). */
export async function transcribeOnMac(
  pcm: Int16Array,
  o: { callId: string; utteranceId: string; dictation?: boolean; send: AudioSend },
): Promise<string> {
  const frames = pcmFrames(pcm)
  let text = ''
  for (let seq = 0; seq < frames.length; seq++) {
    const last = seq === frames.length - 1
    const r = await o.send({
      callId: o.callId,
      utteranceId: o.utteranceId,
      seq,
      pcm: base64(frames[seq]!),
      last,
      ...(o.dictation ? { dictation: true } : {}),
    })
    if (last) text = r?.text?.trim() ?? ''
  }
  return text
}

/**
 * Energy voice-activity detection for the open mic on the Mac path: speech starts after
 * `startMs` above the threshold and ends after `endMs` of silence. Only speech is sent.
 */
export class EnergyVad {
  private above = 0
  private below = 0
  speaking = false
  constructor(private readonly o: { threshold?: number; startMs?: number; endMs?: number } = {}) {}
  /** Feed one 16 kHz frame; returns a transition. */
  push(frame: Int16Array): 'start' | 'end' | null {
    let sum = 0
    for (let i = 0; i < frame.length; i++) sum += (frame[i]! / 0x8000) ** 2
    const rms = Math.sqrt(sum / Math.max(1, frame.length))
    const ms = (frame.length / CALL_AUDIO_SAMPLE_RATE) * 1000
    if (rms >= (this.o.threshold ?? 0.02)) {
      this.above += ms
      this.below = 0
      if (!this.speaking && this.above >= (this.o.startMs ?? 150)) {
        this.speaking = true
        return 'start'
      }
    } else {
      this.below += ms
      this.above = 0
      if (this.speaking && this.below >= (this.o.endMs ?? 600)) {
        this.speaking = false
        return 'end'
      }
    }
    return null
  }
}

export interface PcmRecorder {
  /** Stop and return everything captured (16 kHz Int16). */
  stop(): Promise<Int16Array>
  cancel(): void
}

/**
 * Capture the microphone as 16 kHz PCM, in memory only. `onFrame` sees every ~250 ms chunk
 * (VAD); capture ends itself at 30 s.
 */
export async function recordPcm16(
  o: { onFrame?: (f: Int16Array) => void; maxMs?: number; onAutoStop?: () => void } = {},
): Promise<PcmRecorder> {
  const media = await navigator.mediaDevices.getUserMedia({
    audio: { echoCancellation: true, noiseSuppression: true, channelCount: 1 },
  })
  const Ctx =
    (globalThis as { AudioContext?: typeof AudioContext }).AudioContext ??
    (globalThis as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext
  const ctx = new Ctx()
  const src = ctx.createMediaStreamSource(media)
  // ScriptProcessor (not an AudioWorklet): no blob/module URL, so it works under the app's CSP.
  const proc = ctx.createScriptProcessor(4096, 1, 1)
  const chunks: Int16Array[] = []
  let samples = 0
  const max = ((o.maxMs ?? 30_000) / 1000) * CALL_AUDIO_SAMPLE_RATE
  let done = false
  proc.onaudioprocess = (e) => {
    if (done) return
    const pcm = downsampleTo16k(e.inputBuffer.getChannelData(0), ctx.sampleRate)
    chunks.push(pcm)
    samples += pcm.length
    o.onFrame?.(pcm)
    if (samples >= max) o.onAutoStop?.()
  }
  src.connect(proc)
  proc.connect(ctx.destination)
  const close = () => {
    done = true
    proc.disconnect()
    src.disconnect()
    for (const t of media.getTracks()) t.stop()
    void ctx.close().catch(() => {})
  }
  return {
    async stop() {
      close()
      const out = new Int16Array(Math.min(samples, max))
      let at = 0
      for (const c of chunks) {
        if (at >= out.length) break
        out.set(c.subarray(0, out.length - at), at)
        at += c.length
      }
      chunks.length = 0
      return out
    },
    cancel() {
      close()
      chunks.length = 0
    },
  }
}
